import { Card } from '@/components/ui/card';
import { ContentImageField } from '@/components/admin/content-image-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Wind, Activity, Music, Play, Plus, Pencil, Trash2 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { normalizeImageUrl } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { useProfile } from '@/lib/providers';
import { AdminRole, ContentConfig, ContentConfigInsert, ContentConfigEditOrInsert } from '@/types/custom';
import { useToast } from '@/components/ui/use-toast';
import { useExercises, Exercise, ExerciseInsert, BreathingPhase } from '@/lib/hooks/use-exercises';
import { useContentWithConfig } from '@/lib/hooks/use-content';
import MeditationPlayer from '@/components/meditation/meditation-player';
import ConfigEditView, { nameConfig } from '@/components/meditation/config-edit';
import { supabase } from '@/lib/supabase';
import Loading from '@/components/loading';
import {
  Root as TabsRoot,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@radix-ui/react-tabs';
import { Switch } from '@/components/ui/switch';

interface CategoryMeta {
  key: string;
  icon: LucideIcon;
  title: string;
  imageUrl: string;
}

const CATEGORIES: CategoryMeta[] = [
  {
    key: 'ademhaling',
    icon: Wind,
    title: 'Ademhalingsoefeningen',
    imageUrl: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200',
  },
  {
    key: 'lichaamsbewustzijn',
    icon: Activity,
    title: 'Lichaamsbewustzijnsoefeningen',
    imageUrl: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1200',
  },
  {
    key: 'dance',
    icon: Music,
    title: 'Dance-Breaks',
    imageUrl: 'https://images.unsplash.com/photo-1524594152303-9fd13543fe6e?q=80&w=1200',
  },
];

/* ─── Breathing circle overlay (synced to audio time) ─── */

function BreathingOverlay({
  phases,
  bpm,
  introDelay,
  totalRounds,
  audioTime,
  audioPlaying,
}: {
  phases: BreathingPhase[];
  bpm: number;
  introDelay: number;
  totalRounds: number;
  audioTime: number;
  audioPlaying: boolean;
}) {
  const beatDuration = 60 / bpm;
  const phaseDurations = phases.map((p) => p.duration * beatDuration);
  const roundDuration = phaseDurations.reduce((a, b) => a + b, 0);
  const totalDuration = roundDuration * totalRounds;

  const inIntro = audioTime < introDelay;
  const elapsed = Math.max(0, audioTime - introDelay);
  const finished = elapsed >= totalDuration;

  // Find current round and phase
  let currentRound = Math.min(Math.floor(elapsed / roundDuration), totalRounds - 1);
  let timeInRound = elapsed - currentRound * roundDuration;

  if (finished) {
    currentRound = totalRounds - 1;
    timeInRound = roundDuration;
  }

  let phaseIdx = 0;
  let phaseElapsed = timeInRound;
  for (let i = 0; i < phaseDurations.length; i++) {
    if (phaseElapsed < phaseDurations[i]) {
      phaseIdx = i;
      break;
    }
    phaseElapsed -= phaseDurations[i];
    if (i === phaseDurations.length - 1) phaseIdx = i;
  }

  const currentPhase = phases[phaseIdx];
  const phaseDur = phaseDurations[phaseIdx];
  const phaseProgress = phaseDur > 0 ? Math.min(phaseElapsed / phaseDur, 1) : 0;

  // Circle scale: full at end of inhale, stays during hold, shrinks during exhale
  let circleScale = 0;
  if (inIntro || finished) {
    circleScale = 0;
  } else if (currentPhase.action === 'inhale') {
    circleScale = phaseProgress;
  } else if (currentPhase.action === 'exhale') {
    circleScale = 1 - phaseProgress;
  } else if (currentPhase.action === 'hold') {
    // Hold at the level from previous phase — no changes
    const prevIdx = phaseIdx > 0 ? phaseIdx - 1 : phases.length - 1;
    circleScale = phases[prevIdx].action === 'inhale' ? 1 : 0;
  }

  const circleSize = 120 + circleScale * 120;

  // Count up from 1 per beat
  const beatsElapsed = Math.floor(phaseElapsed / beatDuration) + 1;
  const totalBeats = Math.round(phaseDur / beatDuration);
  const beatCount = Math.min(beatsElapsed, totalBeats);

  // Determine label
  let label = '';
  if (finished) label = 'Klaar!';
  else if (inIntro) label = 'Luister naar de introductie...';
  else label = currentPhase.label;

  return (
    <div className="flex flex-col items-center py-4">
      <div className="relative flex items-center justify-center" style={{ height: 240, width: 240 }}>
        {/* Outer glow */}
        <div
          className="absolute rounded-full transition-all duration-[1400ms] ease-in-out"
          style={{
            width: circleSize + 20,
            height: circleSize + 20,
            background: 'radial-gradient(circle, rgba(74,222,128,0.15) 0%, transparent 70%)',
          }}
        />
        {/* Main circle */}
        <div
          className="rounded-full flex items-center justify-center transition-all duration-[1400ms] ease-in-out border-2 border-green-400/50"
          style={{
            width: circleSize,
            height: circleSize,
            background: `radial-gradient(circle, rgba(74,222,128,${0.15 + circleScale * 0.2}) 0%, rgba(74,222,128,${0.05 + circleScale * 0.1}) 100%)`,
            boxShadow: `0 0 ${30 + circleScale * 30}px rgba(74,222,128,${0.2 + circleScale * 0.2})`,
          }}
        >
          <div className="text-center">
            <p className="text-white text-lg font-semibold">{label}</p>
            {!inIntro && !finished && (
              <p className="text-white/80 text-3xl font-bold mt-1">{beatCount}</p>
            )}
          </div>
        </div>
      </div>

      {/* Round indicator */}
      {!inIntro && (
        <div className="mt-4 flex items-center gap-2">
          {Array.from({ length: totalRounds }).map((_, i) => (
            <div
              key={i}
              className={`h-2 w-2 rounded-full transition-colors ${
                i <= currentRound && !inIntro ? 'bg-green-400' : 'bg-white/30'
              }`}
            />
          ))}
          <span className="text-white/60 text-sm ml-2">
            Ronde {Math.min(currentRound + 1, totalRounds)}/{totalRounds}
          </span>
        </div>
      )}
    </div>
  );
}

/* ─── Instruction overlay (synced to audio) ─── */

function InstructionOverlay({
  instructions,
  audioTime,
  totalDuration,
}: {
  instructions: string[];
  audioTime: number;
  totalDuration: number;
}) {
  // Auto-advance steps based on audio time (evenly distributed)
  const stepDuration = totalDuration > 0 ? totalDuration / instructions.length : 10;
  const currentStep = Math.min(
    Math.floor(audioTime / stepDuration),
    instructions.length - 1,
  );

  return (
    <div className="bg-black/40 backdrop-blur-sm rounded-lg border border-white/10 p-4 space-y-3">
      {instructions.map((step, i) => (
        <div
          key={i}
          className={`flex items-start gap-3 transition-opacity duration-300 ${
            i <= currentStep ? 'opacity-100' : 'opacity-30'
          }`}
        >
          <div
            className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${
              i < currentStep
                ? 'bg-green-500 text-white'
                : i === currentStep
                  ? 'bg-green-500/80 text-white ring-2 ring-green-400/50'
                  : 'bg-white/20 text-white/60'
            }`}
          >
            {i + 1}
          </div>
          <p className={`text-sm leading-relaxed ${i <= currentStep ? 'text-white' : 'text-white/50'}`}>
            {step}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ─── Exercise player (wraps MeditationPlayer) ─── */

function ExercisePlayer({
  exercise,
  onClose,
}: {
  exercise: Exercise;
  onClose: () => void;
}) {
  const { contentWithConfig, loading } = useContentWithConfig(exercise.content_id ?? '');
  const [audioTime, setAudioTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  const handleTimeUpdate = useCallback((time: number) => {
    setAudioTime(time);
  }, []);

  const handlePlayStateChange = useCallback((playing: boolean) => {
    setAudioPlaying(playing);
  }, []);

  if (!exercise.content_id) {
    // No audio linked — show a message
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <Card className="p-8 max-w-md text-center space-y-4">
          <h2 className="text-xl font-semibold">{exercise.title}</h2>
          <p className="text-muted-foreground">{exercise.description}</p>
          <p className="text-sm text-muted-foreground">
            Er is nog geen audio gekoppeld aan deze oefening.
          </p>
          <Button variant="outline" onClick={onClose}>
            Terug
          </Button>
        </Card>
      </div>
    );
  }

  if (loading || !contentWithConfig) {
    return <Loading />;
  }

  // Track total duration from content configs
  const overlay =
    exercise.exercise_type === 'breathing' && exercise.phases ? (
      <BreathingOverlay
        phases={exercise.phases}
        bpm={exercise.bpm}
        introDelay={exercise.intro_duration}
        totalRounds={exercise.rounds ?? 9}
        audioTime={audioTime}
        audioPlaying={audioPlaying}
      />
    ) : exercise.instructions && exercise.instructions.length > 0 ? (
      <InstructionOverlay
        instructions={exercise.instructions}
        audioTime={audioTime}
        totalDuration={audioDuration}
      />
    ) : null;

  const isBreathing = exercise.exercise_type === 'breathing';

  return (
    <MeditationPlayer
      contentData={contentWithConfig}
      onNext={onClose}
      onBack={onClose}
      fullscreenContent={overlay}
      hideArtwork={isBreathing}
      autoShowTranscript={!isBreathing}
      onTimeUpdate={handleTimeUpdate}
      onPlayStateChange={handlePlayStateChange}
    />
  );
}

/* ─── Admin edit dialog ─── */

const EMPTY_CONFIG: Partial<ContentConfigEditOrInsert> = {
  audio_link: '',
  music: true,
  voice: false,
  voice_name: null,
  transcription_url: null,
};

function ExerciseEditDialog({
  open,
  onOpenChange,
  exercise,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exercise: Exercise | null;
  onSave: (data: ExerciseInsert) => Promise<void>;
}) {
  const { toast } = useToast();

  // Exercise fields
  const [category, setCategory] = useState('ademhaling');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [exerciseType, setExerciseType] = useState('breathing');
  const [phasesJson, setPhasesJson] = useState('');
  const [rounds, setRounds] = useState('9');
  const [instructionsText, setInstructionsText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [introDuration, setIntroDuration] = useState('75');
  const [bpm, setBpm] = useState('80');
  const [published, setPublished] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Audio configs (same pattern as MeditationEdit)
  const { contentWithConfig, loading: loadingConfigs } = useContentWithConfig(exercise?.content_id ?? '');
  const [prevConfigs, setPrevConfigs] = useState<ContentConfig[]>([]);
  const [newConfigs, setNewConfigs] = useState<ContentConfigEditOrInsert[]>([]);
  const [viewingConfigIdx, setViewingConfigIdx] = useState(-1);
  const [configsInitialized, setConfigsInitialized] = useState(false);

  // Init exercise fields + reset configs flag when dialog opens/closes
  useEffect(() => {
    setConfigsInitialized(false);
    if (exercise) {
      setCategory(exercise.category);
      setTitle(exercise.title);
      setDescription(exercise.description);
      setExerciseType(exercise.exercise_type);
      setPhasesJson(exercise.phases ? JSON.stringify(exercise.phases, null, 2) : '');
      setRounds(exercise.rounds?.toString() ?? '9');
      setInstructionsText(exercise.instructions ? exercise.instructions.join('\n') : '');
      setImageUrl(exercise.image_url ?? '');
      setIntroDuration(exercise.intro_duration?.toString() ?? '75');
      setBpm(exercise.bpm?.toString() ?? '80');
    } else {
      setCategory('ademhaling');
      setTitle('');
      setDescription('');
      setExerciseType('breathing');
      setPhasesJson('[\n  {"label": "Adem in", "duration": 4, "action": "inhale"},\n  {"label": "Vasthouden", "duration": 4, "action": "hold"},\n  {"label": "Adem uit", "duration": 4, "action": "exhale"}\n]');
      setRounds('9');
      setInstructionsText('');
      setImageUrl('');
      setIntroDuration('75');
      setBpm('40');
      setPublished(false);
      setPrevConfigs([]);
      setNewConfigs([]);
      setViewingConfigIdx(-1);
    }
  }, [exercise, open]);

  // Load existing content configs once when editing (not on every refetch)
  useEffect(() => {
    if (contentWithConfig && !configsInitialized) {
      setConfigsInitialized(true);
      setPrevConfigs(contentWithConfig.configs || []);
      setNewConfigs(structuredClone(contentWithConfig.configs || []));
      setViewingConfigIdx(contentWithConfig.configs?.length ? 0 : -1);
      setPublished(contentWithConfig.content.published ?? false);
    }
  }, [contentWithConfig, configsInitialized]);

  useEffect(() => {
    if (category === 'ademhaling') setExerciseType('breathing');
    else if (category === 'lichaamsbewustzijn') setExerciseType('body');
    else if (category === 'dance') setExerciseType('dance');
  }, [category]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      let phases: BreathingPhase[] | null = null;
      let instructions: string[] | null = null;

      if (exerciseType === 'breathing' && phasesJson.trim()) {
        phases = JSON.parse(phasesJson);
      }
      if (exerciseType !== 'breathing' && instructionsText.trim()) {
        instructions = instructionsText.split('\n').filter((l) => l.trim());
      }

      // Determine content type for the content entry
      const contentType = exerciseType === 'breathing' ? 'breathing'
        : exerciseType === 'body' ? 'body_exercise'
        : 'dance_break';

      let contentId = exercise?.content_id ?? null;

      // Create or update the content entry for audio
      if (contentId) {
        // Update existing content entry
        const { error } = await supabase
          .from('content')
          .update({
            title: title.trim(),
            description: description.trim(),
            image_url: imageUrl.trim() || null,
            content_type: contentType,
            published,
          })
          .eq('id', contentId);
        if (error) throw error;
      } else if (newConfigs.length > 0) {
        // Create new content entry only if configs were added
        const { data, error } = await supabase
          .from('content')
          .insert({
            title: title.trim(),
            description: description.trim(),
            image_url: imageUrl.trim() || null,
            content_type: contentType,
            published,
          })
          .select('id')
          .single();
        if (error) throw error;
        contentId = data.id;
      }

      // Save audio configs
      if (contentId) {
        // Delete removed configs
        const configsToDelete = prevConfigs.filter(
          (c) => !newConfigs.some((nc) => nc.id === c.id),
        );
        if (configsToDelete.length > 0) {
          await supabase
            .from('content_configs')
            .delete()
            .in('id', configsToDelete.map((c) => c.id));
        }

        // Create new configs
        const configsToCreate = newConfigs.filter((c) => !c.id);
        console.log(`[ExerciseEdit] Creating ${configsToCreate.length} configs, updating ${newConfigs.filter((c) => c.id).length}, deleting ${configsToDelete.length}`);
        for (const config of configsToCreate) {
          const payload: ContentConfigInsert = {
            content_id: contentId,
            audio_link: config.audio_link || '',
            music: config.music,
            voice: config.voice,
            voice_name: config.voice_name,
            transcription_url: config.transcription_url,
          };
          console.log('[ExerciseEdit] Inserting config:', payload);
          const { error } = await supabase.from('content_configs').insert(payload);
          if (error) {
            console.error('[ExerciseEdit] Config insert error:', error);
            throw error;
          }
        }

        // Update existing configs
        const configsToUpdate = newConfigs.filter((c) => c.id);
        for (const config of configsToUpdate) {
          const { error } = await supabase
            .from('content_configs')
            .update({
              audio_link: config.audio_link,
              music: config.music,
              voice: config.voice,
              voice_name: config.voice_name,
              transcription_url: config.transcription_url,
            })
            .eq('id', config.id);
          if (error) throw error;
        }
      }

      // Save exercise
      await onSave({
        category,
        title: title.trim(),
        description: description.trim(),
        exercise_type: exerciseType,
        phases,
        rounds: exerciseType === 'breathing' ? parseInt(rounds) || 9 : null,
        instructions,
        image_url: imageUrl.trim() || null,
        sort_order: exercise?.sort_order ?? 0,
        content_id: contentId,
        intro_duration: parseInt(introDuration) || 0,
        bpm: parseInt(bpm) || 60,
      });
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: 'Fout bij opslaan', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = exercise ? 'Oefening bewerken' : 'Nieuwe oefening';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pageTitle}</DialogTitle>
          <DialogDescription className="sr-only">{pageTitle}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-2">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Categorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ex-title">Titel</Label>
              <Input id="ex-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Naam van de oefening" />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="ex-desc">Beschrijving</Label>
            <Textarea id="ex-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Korte beschrijving..." rows={2} />
          </div>

          <ContentImageField
            id="ex-image"
            value={imageUrl}
            onChange={setImageUrl}
            label="Afbeelding"
          />

          {/* Exercise-specific settings */}
          {exerciseType === 'breathing' ? (
            <>
              <div className="grid gap-2">
                <Label htmlFor="ex-phases">Ademhalingsfases (JSON)</Label>
                <Textarea
                  id="ex-phases"
                  value={phasesJson}
                  onChange={(e) => setPhasesJson(e.target.value)}
                  rows={6}
                  className="font-mono text-xs"
                  placeholder='[{"label":"Adem in","duration":4,"action":"inhale"}]'
                />
                <p className="text-xs text-muted-foreground">
                  Acties: inhale, hold, exhale. Duration = aantal tellen bij het ingestelde BPM.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="ex-rounds">Rondes</Label>
                  <Input id="ex-rounds" type="number" value={rounds} onChange={(e) => setRounds(e.target.value)} min="1" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ex-bpm">BPM</Label>
                  <Input id="ex-bpm" type="number" value={bpm} onChange={(e) => setBpm(e.target.value)} min="1" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ex-intro">Intro (sec)</Label>
                  <Input id="ex-intro" type="number" value={introDuration} onChange={(e) => setIntroDuration(e.target.value)} min="0" />
                </div>
              </div>
            </>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="ex-instructions">Instructies (één per regel)</Label>
              <Textarea
                id="ex-instructions"
                value={instructionsText}
                onChange={(e) => setInstructionsText(e.target.value)}
                rows={6}
                placeholder={"Stap 1...\nStap 2...\nStap 3..."}
              />
            </div>
          )}

          {/* Audio Configurations — same as MeditationEdit */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-sm">Audio Configuraties ({newConfigs.length})</h3>
            {exercise?.content_id && loadingConfigs && !configsInitialized ? (
              <p className="text-sm text-muted-foreground">Configuraties laden...</p>
            ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                const isMusic = newConfigs.filter((c) => c.music).length === 0;
                setNewConfigs([
                  ...newConfigs,
                  {
                    ...structuredClone(EMPTY_CONFIG),
                    content_id: exercise?.content_id || '',
                    music: isMusic,
                    voice: !isMusic,
                  } as ContentConfigEditOrInsert,
                ]);
                setViewingConfigIdx(newConfigs.length);
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Configuratie toevoegen
            </Button>
            )}

            {newConfigs.length > 0 && (
              <TabsRoot
                value={String(viewingConfigIdx)}
                onValueChange={(newTab) => setViewingConfigIdx(Number(newTab))}
              >
                <TabsList className="flex border-b">
                  {newConfigs.map((config, idx) => (
                    <TabsTrigger
                      value={String(idx)}
                      key={idx}
                      className="px-4 py-2 -mb-px text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-800 hover:border-gray-300 focus:outline-none data-[state=active]:text-green-600 data-[state=active]:border-green-500"
                    >
                      {nameConfig(config)}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {newConfigs.map((config, idx) => (
                  <TabsContent value={String(idx)} key={idx} className="pt-4">
                    <ConfigEditView
                      musicAllowed={newConfigs.filter((c) => c.music).length === 0 || config.music}
                      config={config}
                      onUpdate={(updatedConfig) => {
                        setNewConfigs((prev) => {
                          const tmp = structuredClone(prev);
                          tmp[idx] = updatedConfig;
                          return tmp;
                        });
                      }}
                      onDelete={() => {
                        setNewConfigs((prev) => prev.filter((_, i) => i !== idx));
                        setViewingConfigIdx((prevIdx) => Math.max(0, prevIdx - 1));
                      }}
                    />
                  </TabsContent>
                ))}
              </TabsRoot>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="ex-published"
              checked={published}
              onCheckedChange={setPublished}
              className={`${published ? 'bg-green-500' : ''} data-[state=checked]:bg-green-500`}
            />
            <Label htmlFor="ex-published">Gepubliceerd</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()} className="bg-green-600 hover:bg-green-700 text-white">
            {submitting ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main page ─── */

export default function OefeningenPage() {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;
  const { toast } = useToast();
  const { exercises, loading, createExercise, updateExercise, deleteExercise } = useExercises();

  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [checkedActief, setCheckedActief] = useState(false);

  // Auto-open exercise from ?actief=<content_id> query param (e.g. from favorites)
  useEffect(() => {
    if (checkedActief || loading || exercises.length === 0) return;
    setCheckedActief(true);
    const params = new URLSearchParams(window.location.search);
    const actiefContentId = params.get('actief');
    if (actiefContentId) {
      const found = exercises.find((e) => e.content_id === actiefContentId);
      if (found) setSelectedExercise(found);
    }
  }, [exercises, loading, checkedActief]);

  if (loadingProfile || loading) return <Loading />;

  if (selectedExercise) {
    return (
      <ExercisePlayer
        exercise={selectedExercise}
        onClose={() => setSelectedExercise(null)}
      />
    );
  }

  const handleSave = async (data: ExerciseInsert) => {
    if (editingExercise) {
      await updateExercise(editingExercise.id, data);
      toast({ title: 'Oefening bijgewerkt' });
    } else {
      const categoryExercises = exercises.filter((e) => e.category === data.category);
      data.sort_order = categoryExercises.length;
      await createExercise(data);
      toast({ title: 'Oefening aangemaakt' });
    }
  };

  const handleDelete = async (exercise: Exercise) => {
    if (!confirm(`'${exercise.title}' verwijderen?`)) return;
    await deleteExercise(exercise.id);
    toast({ title: 'Oefening verwijderd', description: `'${exercise.title}' is verwijderd.` });
  };

  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Oefeningen</h1>
        {isAdmin && (
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => {
              setEditingExercise(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Toevoegen
          </Button>
        )}
      </div>
      <p className="text-muted-foreground max-w-2xl">
        Ontdek oefeningen die je helpen om tot rust te komen, je lichaam te
        voelen en energie op te laden.
      </p>

      {CATEGORIES.map((catMeta) => {
        const Icon = catMeta.icon;
        const catExercises = exercises.filter((e) => e.category === catMeta.key);

        if (catExercises.length === 0 && !isAdmin) return null;

        return (
          <section key={catMeta.key} className="space-y-4 max-w-6xl">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold">{catMeta.title}</h2>
            </div>

            {catExercises.length === 0 && isAdmin && (
              <p className="text-sm text-muted-foreground">Nog geen oefeningen in deze categorie.</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {catExercises.map((item) => (
                <Card
                  key={item.id}
                  className={`overflow-hidden flex flex-col transition-shadow ${item.content_id ? 'cursor-pointer hover:shadow-lg' : 'opacity-80'}`}
                  onClick={() => item.content_id ? setSelectedExercise(item) : undefined}
                >
                  <div className="relative h-40">
                    <img
                      src={normalizeImageUrl(item.image_url) || catMeta.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      {item.content_id ? (
                        <Icon className="h-10 w-10 text-white" />
                      ) : (
                        <span className="text-white font-semibold text-sm bg-black/50 px-3 py-1.5 rounded-full">
                          Binnenkort beschikbaar
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5 flex flex-col flex-grow">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold">{item.title}</h3>
                      {isAdmin && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingExercise(item);
                              setDialogOpen(true);
                            }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item);
                            }}
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                      {item.description}
                    </p>
                    <span className="mt-3 text-sm font-medium text-green-600">
                      Start oefening
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        );
      })}

      <ExerciseEditDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingExercise(null);
        }}
        exercise={editingExercise}
        onSave={handleSave}
      />
    </div>
  );
}
